'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@common/components';
import { useToast } from '@common/hooks';
import { SvgGiftBoxRed } from '@common/icon';
import { cn } from '@core/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { AuthMutation, AuthQueryOptions } from '@/api';
import { useIsGuest } from '@/hooks/useIsGuest';
import { useAdpopcornSspFullscreenAd } from '@/modules/adpopcorn-ssp';
import { ADPOPCORN_SSP_MODULE } from '@/modules/wello-app';
import { useAdpopcornSspPlacementId } from '../../_hooks/useAdpopcornSspPlacementId';
import { useBenefitGate } from '../../_hooks/useBenefitGate';
import { useCountdown } from '../../_hooks/useCountdown';
import { resolveAdTriggerNotice } from '../../_utils/adTriggerNotice';
import { type CountdownState, formatCountdown } from '../../_utils/countdown';
import { useBonusSlot } from '../_hooks/useBonusSlot';
const AdBonusSlotStatus = t0 => {
  const $ = _c(14);
  const {
    slot
  } = t0;
  const t1 = slot.isAvailable ? "\uC9C0\uAE08 \uBC14\uB85C \uCC38\uC5EC\uD560 \uC218 \uC788\uC5B4\uC694" : "\uB2E4\uC74C \uBCF4\uB108\uC2A4\uAE4C\uC9C0 \uB0A8\uC740 \uC2DC\uAC04";
  let t2;
  if ($[0] !== t1) {
    t2 = <p className="font-600 text-Gray-600 typo-caption1">{t1}</p>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = slot.isAvailable;
  let t4;
  if ($[2] !== slot.isAvailable) {
    t4 = cn("font-800 tabular-nums text-Gray-black typo-h4", {
      invisible: slot.isAvailable
    });
    $[2] = slot.isAvailable;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== slot.isAvailable || $[5] !== slot.secondsUntilAvailable) {
    t5 = slot.isAvailable ? "00:00:00" : formatCountdown(slot.secondsUntilAvailable);
    $[4] = slot.isAvailable;
    $[5] = slot.secondsUntilAvailable;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== slot.isAvailable || $[8] !== t4 || $[9] !== t5) {
    t6 = <p aria-hidden={t3} suppressHydrationWarning={true} className={t4}>{t5}</p>;
    $[7] = slot.isAvailable;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t2 || $[12] !== t6) {
    t7 = <div className="flex flex-col items-center gap-4 text-center">{t2}{t6}</div>;
    $[11] = t2;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
};
const AdBonusWatchButton = t0 => {
  const $ = _c(34);
  const {
    slot
  } = t0;
  const {
    triggerGate,
    gateModals
  } = useBenefitGate();
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  const placementId = useAdpopcornSspPlacementId(ADPOPCORN_SSP_MODULE.REWARD_VIDEO);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = AuthMutation["post:/wello/v2/auth/external/adpopcorn-ssp"]();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== queryClient || $[2] !== toast) {
    t2 = () => {
      toast({
        message: "\uD589\uC6B4\uAD8C\uC744 \uBC1B\uC558\uC5B4\uC694"
      });
      return Promise.all([queryClient.invalidateQueries({
        queryKey: AuthQueryOptions["get:/wello/v2/auth/benefit-tickets/lucky-ticket"]().queryKey
      }), queryClient.invalidateQueries({
        queryKey: AuthQueryOptions["get:/wello/v2/auth/benefit-tickets"]().queryKey
      })]);
    };
    $[1] = queryClient;
    $[2] = toast;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== toast) {
    t3 = () => {
      toast({
        message: "\uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC138\uC694",
        type: "fail"
      });
    };
    $[4] = toast;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = {
      ...t1,
      onSuccess: t2,
      onError: t3
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    mutate: rewardBonusTicket,
    isPending: isRewarding
  } = useMutation(t4);
  let t5;
  if ($[9] !== placementId || $[10] !== rewardBonusTicket) {
    t5 = {
      autoShow: true,
      onCompleted: () => {
        rewardBonusTicket({
          body: {
            placementId,
            sspType: "REWARDED_VIDEO"
          }
        });
      }
    };
    $[9] = placementId;
    $[10] = rewardBonusTicket;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const {
    load,
    phase
  } = useAdpopcornSspFullscreenAd(ADPOPCORN_SSP_MODULE.REWARD_VIDEO, t5);
  let t6;
  if ($[12] !== load || $[13] !== placementId || $[14] !== triggerGate) {
    t6 = () => {
      triggerGate(["app", "login", "adult", "consent"], () => {
        load(placementId);
      });
    };
    $[12] = load;
    $[13] = placementId;
    $[14] = triggerGate;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const handleWatchAd = t6;
  let t7;
  if ($[16] !== phase) {
    t7 = resolveAdTriggerNotice(phase);
    $[16] = phase;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const adNotice = t7;
  const t8 = !adNotice;
  const t9 = !adNotice;
  let t10;
  if ($[18] !== t9) {
    t10 = cn("text-center text-Gray-500 typo-caption2", {
      invisible: t9
    });
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const t11 = adNotice ?? "\uAD11\uACE0\uB97C \uC900\uBE44\uD558\uACE0 \uC788\uC5B4\uC694";
  let t12;
  if ($[20] !== t10 || $[21] !== t11 || $[22] !== t8) {
    t12 = <p aria-hidden={t8} className={t10}>{t11}</p>;
    $[20] = t10;
    $[21] = t11;
    $[22] = t8;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const t13 = phase === "pending" || isRewarding;
  const t14 = !slot.isAvailable || phase === "cooldown";
  let t15;
  if ($[24] !== handleWatchAd || $[25] !== t13 || $[26] !== t14) {
    t15 = <Button className="w-full" dimensions="H48" loading={t13} disabled={t14} onClick={handleWatchAd}>광고 보고 더 받기</Button>;
    $[24] = handleWatchAd;
    $[25] = t13;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t12 || $[29] !== t15) {
    t16 = <div className="flex w-full flex-col items-center gap-8">{t12}{t15}</div>;
    $[28] = t12;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== gateModals || $[32] !== t16) {
    t17 = <>{t16}{gateModals}</>;
    $[31] = gateModals;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  return t17;
};
const AdBonusSlotContent = () => {
  const $ = _c(8);
  const {
    data
  } = useBonusSlot();
  const slot = useCountdown(data.nextAvailableAtMs);
  let t0;
  if ($[0] !== slot) {
    t0 = <AdBonusSlotStatus slot={slot} />;
    $[0] = slot;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <SvgGiftBoxRed className="size-80" originalFill={true} />;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== slot) {
    t2 = <AdBonusWatchButton slot={slot} />;
    $[3] = slot;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t0 || $[6] !== t2) {
    t3 = <>{t0}{t1}{t2}</>;
    $[5] = t0;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
const AdBonusSlotFallback = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <><div className="flex flex-col items-center gap-4 text-center"><p className="font-600 text-Gray-600 typo-caption1">참여 가능 여부를 확인하고 있어요</p><p className="font-800 text-Gray-black typo-h4">-</p></div><SvgGiftBoxRed className="size-80" originalFill={true} /><Button className="w-full" dimensions="H48" disabled={true}>광고 보고 더 받기</Button></>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};

//* 비회원은 auth 쿼리(useBonusSlot)를 태우지 않는다 — useSuspenseQuery는 게스트 401을
//* Suspense가 못 잡아 라우트 error로 번진다. 항상 참여 가능 상태로 노출하고 버튼의 게이트가 로그인을 유도한다.
const GUEST_BONUS_SLOT: CountdownState = {
  isAvailable: true,
  secondsUntilAvailable: 0
};
const AdBonusSlotGuest = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <><AdBonusSlotStatus slot={GUEST_BONUS_SLOT} /><SvgGiftBoxRed className="size-80" originalFill={true} /><AdBonusWatchButton slot={GUEST_BONUS_SLOT} /></>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export const AdBonusCard = t0 => {
  const $ = _c(4);
  const {
    isGuest: serverIsGuest
  } = t0;
  const isGuest = useIsGuest(serverIsGuest);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <p className="font-600 text-Gray-600 typo-caption1">오늘의 미션을 다 하셨다면?</p>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex flex-col gap-8 text-center">{t1}<div className="flex flex-col font-800 typo-h4"><p className="text-O-500">지금 광고 보고</p><p className="text-Gray-900">행운권 추가로 더 받기</p></div></div>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== isGuest) {
    t3 = <section className="flex flex-col gap-20 bg-O-25 px-20 py-32 xl:rounded-small">{t2}<div className="flex flex-col items-center gap-20 rounded-small bg-Gray-white px-20 py-16">{isGuest ? <AdBonusSlotGuest /> : <ErrorBoundary fallback={<AdBonusSlotFallback />}><Suspense fallback={<AdBonusSlotFallback />}><AdBonusSlotContent /></Suspense></ErrorBoundary>}</div></section>;
    $[2] = isGuest;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};